import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { Button, Form, Input, Space, Select } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { handleDragEnd, handleDragOver } from "../../helpers/dragHelpers"
import { MatchColumnsDroppable } from "./MatchColumnsDroppable"
import { useLesson } from "../../hooks/lesson-context"

export function MatchColumns({
  question,
  questionIndex,
  form,
  dispatch,
  isFacilitate = false,
  disabled = false,
  inReview = false,
  trackQuestionSubmission,
}) {
  const { state: ctxState, dispatch: ctxDispatch } = useLesson()
  const [activeId, setActiveId] = useState(null)
  const [hasTouched, setHasTouched] = useState(false)
  const [hasDragEnded, setHasDragEnded] = useState(false)
  const lastDragOverRef = useRef(null)
  const [isDesktop, setIsDesktop] = useState(true)

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 992px)")
    setIsDesktop(mql.matches)
    const handler = (e) => setIsDesktop(e.matches)
    mql.addEventListener("change", handler)
    return () => mql.removeEventListener("change", handler)
  }, [])

  const [itemGroups, setItemGroups] = useState(() => {
    const answers = form.getFieldValue(question.uid) || []
    const ansUids = answers.map((x) => x?.uid).filter(Boolean) || []

    const filteredQuestions = question.answers.filter((option) => !ansUids.includes(option?.uid))

    return {
      answers,
      options: filteredQuestions,
    }
  })

  useEffect(() => {
    const validAnswers = itemGroups?.answers?.filter(Boolean) || []
    if (validAnswers.length < question.answers?.length) {
    } else {
      if (hasTouched && hasDragEnded) {
        if (!ctxState?.actionRunning) {
          trackQuestionSubmission({
            data: validAnswers,
            typeUid: question.uid,
            type: question.type,
          })
          setHasDragEnded(false)
        }
      }
    }
    form.setFieldsValue({
      [question.uid]: validAnswers,
    })
  }, [itemGroups?.answers, hasTouched, hasDragEnded])

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = ({ active }) => {
    setHasDragEnded(false)
    if (!hasTouched) {
      setHasTouched(true)
    }
    setActiveId(active.id)
  }

  const handleDragCancel = () => setActiveId(null)

  const handleSelectChange = (val, i) => {
    setHasTouched(true)
    let newAnswers = [...(itemGroups.answers || [])]
    while (newAnswers.length <= i) {
      newAnswers.push(null)
    }

    if (!val) {
      newAnswers[i] = null
    } else {
      const selectedOption = question.answers.find((a) => a.uid === val)
      const existingIndex = newAnswers.findIndex((a) => a?.uid === val)
      if (existingIndex !== -1) {
        newAnswers[existingIndex] = newAnswers[i]
      }
      newAnswers[i] = selectedOption
    }

    const ansUids = newAnswers.map((x) => x?.uid).filter(Boolean)
    const filteredOptions = question.answers.filter((option) => !ansUids.includes(option?.uid))

    setItemGroups({ answers: newAnswers, options: filteredOptions })
    setHasDragEnded(true) // trigger tracking
  }

  const vertical = true
  const titles = question?.content?.title?.split("[$]").filter((t) => t !== "") || []

  return (
    <>
      <Form.List className="" name={question.uid}>
        {(fields) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Space className="sr-only" key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                <Form.Item hidden {...restField} name={[name, "uid"]}>
                  <Input placeholder="uid" />
                </Form.Item>
                <Form.Item hidden {...restField} name={[name, "correct"]} className="">
                  <Input placeholder="correct" />
                </Form.Item>
                <Form.Item hidden {...restField} name={[name, "title"]} className="">
                  <Input placeholder="title" />
                </Form.Item>
              </Space>
            ))}
          </>
        )}
      </Form.List>

      {inReview ? (
        ""
      ) : isDesktop ? (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragCancel={handleDragCancel}
          onDragOver={({ active, over }) => {
            if (!over) return

            // Create a unique key for this drag over state
            const dragOverKey = `${active.id}-${over.id}-${active.data.current?.sortable?.containerId}-${
              over.data.current?.sortable?.containerId || over.id
            }`

            // Only process if this is a new drag over state
            if (lastDragOverRef.current !== dragOverKey) {
              lastDragOverRef.current = dragOverKey
              handleDragOver({ active, over, setItemGroups })
            }
          }}
          onDragEnd={(props) => {
            lastDragOverRef.current = null
            handleDragEnd({ ...props, setActiveId, setItemGroups, activeId, itemGroups })
            setHasDragEnded(true)
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
            <div>
              <p className="instruction">Selected Answers</p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "45% 55%",
                  width: "100%",
                  height: "100%",
                }}
              >
                <div className="match__list" style={{ padding: "6px 0" }}>
                  {titles.map((title, i) => (
                    <div key={`${title}-${i}`} className="list__item">
                      <div className="missing__opt missing__opt--column">{title}</div>
                      <Button shape="circle" style={{ marginLeft: "8px" }} type="primary">
                        {i + 1}
                      </Button>
                    </div>
                  ))}
                </div>

                <MatchColumnsDroppable
                  vertical={vertical}
                  id="answers"
                  items={itemGroups.answers.filter(Boolean)}
                  activeid={activeId}
                  noBorder={true}
                  style={{
                    display: "flex",
                    marginTop: "0",
                    flexDirection: "column",
                    alignItems: "start",
                    gap: "1.375rem",
                    width: "100%",
                    marginLeft: "1.375rem",
                  }}
                  className="fill__missing empty column dropzone--smallMTop"
                />
              </div>
            </div>
            <div>
              <p className="instruction">Available Options</p>
              <MatchColumnsDroppable
                vertical={vertical}
                id="options"
                items={itemGroups.options}
                activeid={activeId}
                style={
                  vertical
                    ? {
                        flexDirection: "column",
                        alignItems: "start",
                        gap: "1rem",
                      }
                    : {}
                }
                className="dropzone--smallMTop empty"
              />
            </div>
          </div>
        </DndContext>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginTop: "1rem" }}>
          {titles.map((title, i) => {
            const selectedItem = itemGroups.answers[i]
            return (
              <div key={`${title}-${i}`} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div style={{ fontWeight: "bold" }}>{title}</div>
                <Select
                  value={selectedItem?.uid || null}
                  style={{ width: "100%" }}
                  placeholder="Select a match"
                  allowClear
                  onChange={(val) => handleSelectChange(val, i)}
                  disabled={disabled}
                >
                  {question.answers.map((opt) => (
                    <Select.Option key={opt.uid} value={opt.uid}>
                      {opt.translations?.["1"]?.title || opt.content?.title || "No title found"}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
